import { CSSProperties, RefObject, useEffect, useState } from 'react'

import { PopoverPlacement } from '../Popover'

interface UsePopoverPositionProps {
	triggerRef: RefObject<HTMLElement>
	popoverRef: RefObject<HTMLElement>
	placement: PopoverPlacement
	open: boolean
	arrow: boolean
}

const ARROW_SIZE = 8
const GAP = 12

/**
 * Hook для вычисления позиции popover
 */
export const usePopoverPosition = ({
	triggerRef,
	popoverRef,
	placement,
	open,
	arrow
}: UsePopoverPositionProps): CSSProperties => {
	const [position, setPosition] = useState<CSSProperties>({})

	useEffect(() => {
		if (!open || !triggerRef.current || !popoverRef.current) {
			return
		}

		const updatePosition = () => {
			if (!triggerRef.current || !popoverRef.current) return

			const triggerRect = triggerRef.current.getBoundingClientRect()
			const popoverRect = popoverRef.current.getBoundingClientRect()

			const scrollX = window.scrollX || window.pageXOffset
			const scrollY = window.scrollY || window.pageYOffset

			const gap = arrow ? GAP + ARROW_SIZE : GAP

			let top = 0
			let left = 0

			switch (placement) {
				case 'top':
					top = triggerRect.top + scrollY - popoverRect.height - gap
					left = triggerRect.left + scrollX + triggerRect.width / 2 - popoverRect.width / 2
					break
				case 'topLeft':
					top = triggerRect.top + scrollY - popoverRect.height - gap
					left = triggerRect.left + scrollX
					break
				case 'topRight':
					top = triggerRect.top + scrollY - popoverRect.height - gap
					left = triggerRect.right + scrollX - popoverRect.width
					break

				case 'bottom':
					top = triggerRect.bottom + scrollY + gap
					left = triggerRect.left + scrollX + triggerRect.width / 2 - popoverRect.width / 2
					break
				case 'bottomLeft':
					top = triggerRect.bottom + scrollY + gap
					left = triggerRect.left + scrollX
					break
				case 'bottomRight':
					top = triggerRect.bottom + scrollY + gap
					left = triggerRect.right + scrollX - popoverRect.width
					break

				case 'left':
					top = triggerRect.top + scrollY + triggerRect.height / 2 - popoverRect.height / 2
					left = triggerRect.left + scrollX - popoverRect.width - gap
					break
				case 'leftTop':
					top = triggerRect.top + scrollY
					left = triggerRect.left + scrollX - popoverRect.width - gap
					break
				case 'leftBottom':
					top = triggerRect.bottom + scrollY - popoverRect.height
					left = triggerRect.left + scrollX - popoverRect.width - gap
					break

				case 'right':
					top = triggerRect.top + scrollY + triggerRect.height / 2 - popoverRect.height / 2
					left = triggerRect.right + scrollX + gap
					break
				case 'rightTop':
					top = triggerRect.top + scrollY
					left = triggerRect.right + scrollX + gap
					break
				case 'rightBottom':
					top = triggerRect.bottom + scrollY - popoverRect.height
					left = triggerRect.right + scrollX + gap
					break
			}

			// Корректировка, чтобы popover не выходил за границы экрана
			const viewportWidth = window.innerWidth
			const viewportHeight = window.innerHeight

			if (left + popoverRect.width > viewportWidth + scrollX) {
				left = viewportWidth + scrollX - popoverRect.width - 10
			}
			if (left < scrollX) {
				left = scrollX + 10
			}
			if (top + popoverRect.height > viewportHeight + scrollY) {
				top = viewportHeight + scrollY - popoverRect.height - 10
			}
			if (top < scrollY) {
				top = scrollY + 10
			}

			setPosition({
				position: 'absolute',
				top: `${top}px`,
				left: `${left}px`
			})
		}

		// Обновляем позицию сразу и при изменении размеров окна/скролле
		updatePosition()

		window.addEventListener('resize', updatePosition)
		window.addEventListener('scroll', updatePosition, true)

		return () => {
			window.removeEventListener('resize', updatePosition)
			window.removeEventListener('scroll', updatePosition, true)
		}
	}, [open, placement, arrow, triggerRef, popoverRef])

	return position
}
